import { useEffect, useState } from "react";

export default function AskAI({ email }) {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const [files, setFiles] = useState([]);
  const [industry, setIndustry] = useState("general");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [chartB64, setChartB64] = useState("");
  const [status, setStatus] = useState("");
  const [filesToUse, setFilesToUse] = useState(3);

  // ...imports and component state unchanged

// Add this list near the top of the component file (above the return)
const INDUSTRY_OPTIONS = [
  "Salon / Personal Care",
  "Restaurants (Full-service)",
  "Restaurants (Quick-service)",
  "Retail (Brick & Mortar)",
  "E-commerce",
  "Fitness / Gyms",
  "Healthcare Clinics",
  "Real Estate",
  "Construction / Trades",
  "Professional Services",
  "Education / Tutoring",
  "Hospitality (Hotels/Airbnb)",
  "Auto Services",
  "Nonprofit",
  "General",
  "Other (describe)",
];


  useEffect(() => {
    // load current industry
    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/get-industry/${encodeURIComponent(email)}`);
        const j = await r.json();
        if (j.industry) setIndustry(j.industry);
      } catch (e) {}
    })();
  }, [BACKEND_URL, email]);

  const saveIndustry = async () => {
    const fd = new FormData();
    fd.append("email", email);
    fd.append("industry", industry);
    const r = await fetch(`${BACKEND_URL}/set-industry`, { method: "POST", body: fd });
    if (!r.ok) {
      const j = await r.json();
      alert(j.detail || "Failed to set industry");
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!files?.length) return;
    setStatus("Uploading...");
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("email", email);
      const r = await fetch(`${BACKEND_URL}/upload`, { method: "POST", body: fd });
      const j = await r.json();
      if (!r.ok) {
        setStatus(`Error: ${j.detail}`);
        return;
      }
    }
    setStatus(`Uploaded ${files.length} file(s).`);
  };

  const handleAsk = async (e) => {
    e.preventDefault();
    setStatus("Thinking...");
    setAnswer("");
    setChartB64("");
    const fd = new FormData();
    fd.append("email", email);
    fd.append("question", question);
    fd.append("files_to_use", String(filesToUse));
    const r = await fetch(`${BACKEND_URL}/ask`, { method: "POST", body: fd });
    const j = await r.json();
    if (!r.ok) {
      setStatus(`Error: ${j.detail}`);
      return;
    }
    setAnswer(j.answer || "");
    setChartB64(
      j.chart_png_base64 ||    // current backend
      j.chart_png_b64    ||    // older name just in case
      j.chartBase64      ||    // any legacy variant
      ""
      );
    setStatus(`Used ${j.tokens_used} tokens (Total: ${j.total_tokens})`);
  };

  return (
    <div className="card" style={{ display: "grid", gap: 16 }}>
      {/* Industry selector */}
      <div className="form" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "end" }}>
        <div>
          <div className="label">Industry</div>
          <input
            className="input"
            list="industry-list"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder="Select or start typing…"
          />
          <datalist id="industry-list">
            {INDUSTRY_OPTIONS.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
        </div>
        <button className="btn" onClick={saveIndustry}>
          Save
        </button>
      </div>

      {/* Upload multiple files */}
      <form onSubmit={handleUpload} className="form" style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          className="input"
          type="file"
          accept=".csv,.xls,.xlsx"
          multiple
          onChange={(e) => setFiles(Array.from(e.target.files || []))}
        />
        <button type="submit" className="btn">
          Upload
        </button>
      </form>

      {/* Ask question */}
      <form onSubmit={handleAsk} className="form" style={{ display: "grid", gap: 8 }}>
        <div>
          <div className="label">Ask a question about your data</div>
          <textarea
            className="textarea"
            rows={3}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask a concise question about your data…"
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label className="label" style={{ margin: 0 }}>Use last N files:</label>
          <input
            className="input"
            type="number"
            min={1}
            max={5}
            value={filesToUse}
            onChange={(e) => setFilesToUse(Number(e.target.value))}
            style={{ width: 100 }}
          />
          <button type="submit" className="btn btn-primary" style={{ marginLeft: "auto" }}>
            Ask
          </button>
        </div>
      </form>

      {status && (
        <div className="badge" style={{ width: "fit-content" }}>
          <strong>{status}</strong>
        </div>
      )}

      {answer && (
        <div
          className="card"
          style={{
            background: "transparent",
            border: "1px solid var(--panel-border)",
            padding: 12,
            borderRadius: 12,
            maxHeight: 420,
            overflow: "auto",
            whiteSpace: "pre-wrap",
            lineHeight: 1.5,
          }}
        >
          {answer}
        </div>
      )}

      {chartB64 && (
        <div className="chart-wrap">
          <img
            key={chartB64.slice(0, 24)}           // forces repaint if React reuses node
            alt="AI Chart"
            className="chart-img"
            src={`data:image/png;base64,${chartB64}`}
          />
        </div>
      )}
    </div>
  );
}
